import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import api from "../services/api";

const Settings = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [settings, setSettings] = useState({
    displayName: "",
    notificationEmail: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      console.log("Fetching settings...");
      const response = await api.get("/settings");
      console.log("Settings response:", response.data);

      if (response.data.success) {
        setSettings({
          displayName: response.data.user.supportSettings?.displayName || "",
          notificationEmail:
            response.data.user.supportSettings?.notificationEmail || "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "設定の読み込みに失敗しました";
      setError(`設定の読み込みに失敗しました: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      console.log("Saving settings:", settings);

      const response = await api.put("/settings/support", settings);

      if (response.data.success) {
        setSuccess(t("settings.saveSuccess"));
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "設定の保存に失敗しました";
      setError(`設定の保存に失敗しました: ${errorMessage}`);
      setTimeout(() => setError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Support or admin only
  if (user?.role !== "support" && user?.role !== "admin") {
    return <Alert severity="error">{t("settings.accessDenied")}</Alert>;
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        {t("settings.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {t("settings.subtitle")}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("settings.accountInfo")}
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t("settings.email")}
          </Typography>
          <Typography variant="body1">{user?.email}</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t("settings.role")}
          </Typography>
          <Typography variant="body1">
            {user?.role === "admin"
              ? t("settings.roleAdmin")
              : user?.role === "support"
              ? t("settings.roleSupport")
              : t("settings.roleUser")}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          {t("settings.title")}
        </Typography>

        <TextField
          fullWidth
          label={t("settings.displayName")}
          helperText={t("settings.displayNameHelper")}
          value={settings.displayName}
          onChange={(e) =>
            setSettings({ ...settings, displayName: e.target.value })
          }
          margin="normal"
          variant="outlined"
        />

        <TextField
          fullWidth
          label={t("settings.notificationEmail")}
          helperText={t("settings.notificationEmailHelper")}
          type="email"
          value={settings.notificationEmail}
          onChange={(e) =>
            setSettings({ ...settings, notificationEmail: e.target.value })
          }
          margin="normal"
          variant="outlined"
        />

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </Box>
      </Paper>
    </div>
  );
};

export default Settings;
